import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Smartphone } from "lucide-react";
import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import { toast } from "react-toastify";
import AuthShell from "../components/AuthShell";
import { auth } from "../config/firebase";

const PhoneOtpAuth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const [phone, setPhone] = useState(searchParams.get("phone") || "");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);

  const setupRecaptcha = () => {
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(auth, "recaptcha-container", {
        size: "invisible",
        callback: () => {},
      });
    }
  };

  const sendOTP = async (event) => {
    event.preventDefault();

    if (!/^\d{10}$/.test(phone)) {
      toast.error("Please enter a valid 10-digit phone number");
      return;
    }

    try {
      setLoading(true);
      const formattedPhone = `+91${phone}`;

      if (auth.settings.appVerificationDisabledForTesting) {
        toast.info("Test phone detected. Use 123456 as OTP.");
        setOtpSent(true);
        return;
      }

      setupRecaptcha();
      const confirmation = await signInWithPhoneNumber(
        auth,
        formattedPhone,
        window.recaptchaVerifier,
      );
      window.confirmationResult = confirmation;
      setOtpSent(true);
      toast.success("OTP sent successfully");
    } catch (error) {
      console.error("Error sending OTP:", error);
      toast.error("Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async (event) => {
    event.preventDefault();

    if (!/^\d{6}$/.test(otp)) {
      toast.error("Please enter a valid 6-digit OTP");
      return;
    }

    try {
      setLoading(true);

      if (auth.settings.appVerificationDisabledForTesting) {
        if (otp === "123456") {
          localStorage.setItem("verifiedPhone", phone);
          toast.success("Phone number verified");
          navigate("/register-details");
        } else {
          toast.error("Invalid test OTP. Try 123456.");
        }
        return;
      }

      const result = await window.confirmationResult.confirm(otp);
      if (result.user) {
        localStorage.setItem("verifiedPhone", phone);
        toast.success("Phone number verified successfully");
        navigate("/register-details");
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      toast.error("Invalid OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title={otpSent ? "Verify your OTP" : "Secure phone verification"}
      subtitle="This step keeps the JanShakti network trustworthy. Verify your phone once, then we’ll take you straight into profile completion."
      step={otpSent ? 3 : 2}
    >
      <form onSubmit={otpSent ? verifyOTP : sendOTP} className="space-y-6">
        {!otpSent ? (
          <div className="space-y-3 rounded-3xl border border-slate-200 bg-slate-50/70 p-5">
            <label className="block text-sm font-semibold text-slate-700">
              Mobile Number
            </label>
            <div className="relative">
              <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-slate-400">
                <Smartphone className="h-4 w-4" />
              </span>
              <input
                value={phone}
                onChange={(event) => setPhone(event.target.value.replace(/\D/g, ""))}
                className="w-full rounded-2xl border border-slate-200 bg-white py-4 pl-12 pr-4 text-sm outline-none transition focus:border-orange-400"
                placeholder="Enter 10-digit number"
                maxLength="10"
                disabled={loading}
              />
            </div>
            <div className="rounded-2xl border border-orange-100 bg-orange-50/90 p-4 text-sm text-orange-700">
              We’ll send a one-time password to your device. This helps us reduce fake accounts while keeping signup quick.
            </div>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-3 rounded-3xl border border-slate-200 bg-slate-50/70 p-5"
          >
            <div className="flex items-start gap-3 rounded-2xl border border-emerald-100 bg-emerald-50/90 p-4 text-sm text-emerald-800">
              <ShieldCheck className="mt-0.5 h-4 w-4" />
              <p>
                OTP sent to +91 {phone}. Enter the 6-digit code to unlock the final profile step.
              </p>
            </div>
            <label className="block text-sm font-semibold text-slate-700">Enter OTP</label>
            <input
              value={otp}
              onChange={(event) => setOtp(event.target.value.replace(/\D/g, ""))}
              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-4 text-center text-lg tracking-[0.4em] outline-none transition focus:border-orange-400"
              placeholder="------"
              maxLength="6"
              disabled={loading}
            />
            <button
              type="button"
              onClick={() => {
                setOtpSent(false);
                setOtp("");
              }}
              className="text-sm font-semibold text-slate-500 transition hover:text-orange-600"
            >
              Edit phone number
            </button>
          </motion.div>
        )}

        <button
          type="submit"
          className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-orange-500 to-green-500 px-5 py-4 text-sm font-semibold text-white shadow-xl shadow-orange-200/60 transition hover:scale-[1.01] disabled:cursor-not-allowed disabled:opacity-70"
          disabled={loading}
        >
          {loading ? (
            <span className="inline-flex h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
          ) : (
            <ArrowRight className="h-4 w-4" />
          )}
          {otpSent ? "Verify OTP" : "Send OTP"}
        </button>

        <div id="recaptcha-container" />
      </form>
    </AuthShell>
  );
};

export default PhoneOtpAuth;
